import { useState, useEffect } from 'react'
import { api } from '../utils/api'
import { t } from '../i18n/index'
import type { VideoGroup } from '../types'

export default function TimelinePage() {
  const [groups, setGroups] = useState<VideoGroup>({})
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  useEffect(() => {
    api.listVideos().then((res: any) => {
      const g = res.groups || {}
      setGroups(g)
      const dates = Object.keys(g)
      if (dates.length > 0) setSelectedDate(dates[0])
    }).catch(console.error)
  }, [])

  const selectedVideos = selectedDate ? (groups[selectedDate] || []) : []
  const dates = Object.keys(groups).sort().reverse()

  return (
    <div className="flex gap-5 h-full">
      {/* Date sidebar */}
      <div className="w-44 flex-shrink-0 flex flex-col gap-1">
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-1">{t('timeline.dates')}</div>
        {dates.map((date) => (
          <button key={date} onClick={() => setSelectedDate(date)}
            className={`text-left px-3 py-2.5 rounded-sm transition-all duration-100 border-l-2 ${
              selectedDate === date ? 'bg-cyber-primary/10 border-cyber-primary' : 'border-transparent text-gray-500 hover:bg-cyber-primary/5 hover:border-cyber-primary/30'
            }`}>
            <div className={`font-mono text-xs font-bold ${selectedDate === date ? 'text-gray-900' : 'text-gray-600'}`}>{date}</div>
            <div className="font-mono text-[10px] text-gray-400 mt-0.5">{groups[date]?.length || 0} FILES</div>
          </button>
        ))}
      </div>

      {/* Video grid */}
      <div className="flex-1 min-w-0">
        <div className="mb-4">
          <div className="font-mono text-xs font-bold uppercase tracking-widest text-gray-400">{selectedDate || 'SELECT DATE'}</div>
          <div className="font-mono text-[11px] text-gray-400 mt-0.5">{selectedVideos.length} RECORDINGS</div>
        </div>

        {selectedVideos.length === 0 ? (
          <div className="cyber-card p-12 text-center">
            <div className="font-mono text-sm text-gray-400 uppercase tracking-widest">{t('timeline.no-video')}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {selectedVideos.map((v) => (
              <div key={v.id} className="cyber-card group cursor-pointer hover:shadow-md transition-all duration-150 hover:-translate-y-0.5">
                <div className="aspect-video bg-gray-50 border-b border-gray-100 flex items-center justify-center text-2xl relative overflow-hidden">
                  🎬
                  <div className="absolute inset-0 bg-cyber-primary/0 group-hover:bg-cyber-primary/5 transition-colors" />
                  <div className="absolute top-2 right-2 font-mono text-[10px] font-bold bg-black/70 text-white px-1 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">▶</div>
                </div>
                <div className="p-3">
                  <div className="font-mono text-xs font-bold text-gray-900 truncate mb-1.5" title={v.filename}>{v.filename}</div>
                  <div className="flex items-center justify-between">
                    {v.camera_angle && <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 bg-accent-dim text-accent rounded-sm">{v.camera_angle.toUpperCase()}</span>}
                    <span className="font-mono text-[10px] text-gray-400">{v.duration ? `${Math.floor(v.duration / 60)}:${String(Math.floor(v.duration % 60)).padStart(2, '0')}` : '--:--'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
